import { useEffect, useRef, useState, useMemo } from 'react'

const FONT_FAMILY = '"Fusion Pixel", monospace'
// 缝合像素字体原生 12px：先以原生尺寸渲染，再最近邻缩放到目标字高，保证 1 像素 = 1 豆且不糊
const NATIVE_PX = 12

function hexRgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

// 每个色系只选 1 个代表色（深色用于边框 / 浅色用于背景），减少选择负担
function pickByFamily(colors, mode) {
  const families = new Map()
  for (const c of colors) {
    const { r, g, b } = hexRgb(c.hex)
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const sat = max === 0 ? 0 : (max - min) / max
    const luma = 0.299 * r + 0.587 * g + 0.114 * b
    let family
    if (sat < 0.15) {
      family = 'neutral'
    } else {
      // 色相处族（12 个色系）
      const h = rgbToHue(r, g, b)
      family = `h${Math.floor(h / 30)}`
    }
    const prev = families.get(family)
    if (!prev || (mode === 'dark' ? luma < prev.luma : luma > prev.luma)) {
      families.set(family, { color: c, luma })
    }
  }
  return Array.from(families.values()).map((f) => f.color)
}

function rgbToHue(r, g, b) {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  if (d === 0) return 0
  let h
  if (max === r) h = ((g - b) / d) % 6
  else if (max === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4
  return (h * 60 + 360) % 360
}

// 抽屉式色板（与图纸编辑的色板一致的体验）
function SwatchPicker({ value, onChange, label, colors, maxHeight = 'max-h-28' }) {
  const current = colors.find((c) => c.id === value)
  return (
    <div>
      <label className="mb-1 flex items-center justify-between text-xs font-medium text-cocoa-light">
        <span>{label}</span>
        {current && (
          <span className="flex items-center gap-1 text-cocoa">
            <span
              className="inline-block h-3.5 w-3.5 rounded border border-black/10"
              style={{ backgroundColor: current.hex }}
            />
            {current.name}
          </span>
        )}
      </label>
      <div className={`flex flex-wrap gap-1 overflow-y-auto rounded-xl border border-lavender/30 bg-cream-50 p-2 ${maxHeight}`}>
        {colors.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(c.id)}
            className={`h-7 w-7 rounded-md border-2 transition ${
              value === c.id
                ? 'border-blush scale-110 shadow-sm'
                : 'border-black/10 hover:scale-105'
            }`}
            style={{ backgroundColor: c.hex }}
            title={c.name}
          />
        ))}
      </div>
    </div>
  )
}

// 按钮组（替代下拉列表）
function OptionGroup({ value, onChange, label, options }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-cocoa-light">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
              value === o.value
                ? 'border-blush bg-blush text-white'
                : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function drawPattern(canvas, pattern, brand) {
  if (!pattern || !canvas) return
  const { grid, width, height } = pattern
  const cell = Math.max(4, Math.floor(canvas.width / width))
  canvas.width = width * cell
  canvas.height = height * cell
  const ctx = canvas.getContext('2d')
  const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      ctx.fillStyle = colorMap.get(grid[y][x]) || '#fff'
      ctx.fillRect(x * cell, y * cell, cell, cell)
    }
  }
}

export default function TextBeadModal({ open, brand, onClose, onConfirm }) {
  const [text, setText] = useState('豆田')
  const [charHeight, setCharHeight] = useState(12)
  const [textColorId, setTextColorId] = useState(brand.colors[0]?.id)
  const [borderColorId, setBorderColorId] = useState(brand.colors[1]?.id || brand.colors[0]?.id)
  const [bgColorId, setBgColorId] = useState(brand.colors.find((c) => c.hex.toLowerCase() === '#ffffff')?.id || brand.colors[0]?.id)
  const [borderThickness, setBorderThickness] = useState(1)
  const [previewPattern, setPreviewPattern] = useState(null)
  const [fontReady, setFontReady] = useState(false)
  const previewCanvasRef = useRef(null)

  // 边框/背景：每个色系只保留 1 个代表色（深色边框 / 浅色背景）
  const borderColors = useMemo(() => pickByFamily(brand.colors, 'dark'), [brand])
  const bgColors = useMemo(() => pickByFamily(brand.colors, 'light'), [brand])

  // 打开弹窗时预载像素字体，加载完成后重绘
  useEffect(() => {
    if (!open || fontReady) return
    let cancelled = false
    document.fonts?.load(`${NATIVE_PX}px "Fusion Pixel"`).then(() => {
      if (!cancelled) setFontReady(true)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, fontReady])

  const colorMap = new Map(brand.colors.map((c) => [c.id, c]))

  const generatePattern = () => {
    if (!text.trim()) return null

    // 1. 以字体原生 12px 渲染文字
    const tmp = document.createElement('canvas')
    const tmpCtx = tmp.getContext('2d')
    tmpCtx.font = `${NATIVE_PX}px ${FONT_FAMILY}`
    const measured = tmpCtx.measureText(text)
    const textWidthPx = Math.max(
      1,
      Math.ceil(Math.abs(measured.actualBoundingBoxLeft || 0) + Math.abs(measured.actualBoundingBoxRight || measured.width))
    )
    tmp.width = textWidthPx
    tmp.height = NATIVE_PX
    tmpCtx.font = `${NATIVE_PX}px ${FONT_FAMILY}`
    tmpCtx.textBaseline = 'top'
    tmpCtx.fillStyle = '#000000'
    tmpCtx.fillText(text, measured.actualBoundingBoxLeft || 0, 0)

    // 2. 最近邻缩放到目标字高（关闭平滑，保持像素边缘）
    const scale = charHeight / NATIVE_PX
    const textWidthBeads = Math.max(1, Math.round(textWidthPx * scale))
    const tmp2 = document.createElement('canvas')
    tmp2.width = textWidthBeads
    tmp2.height = charHeight
    const tmp2Ctx = tmp2.getContext('2d')
    tmp2Ctx.imageSmoothingEnabled = false
    tmp2Ctx.drawImage(tmp, 0, 0, textWidthBeads, charHeight)
    const imgData = tmp2Ctx.getImageData(0, 0, tmp2.width, tmp2.height)

    const width = textWidthBeads + borderThickness * 2
    const height = charHeight + borderThickness * 2

    const grid = []
    const textColor = textColorId
    const borderColor = borderColorId
    const bgColor = bgColorId

    for (let y = 0; y < height; y++) {
      const row = []
      for (let x = 0; x < width; x++) {
        if (
          x < borderThickness ||
          x >= width - borderThickness ||
          y < borderThickness ||
          y >= height - borderThickness
        ) {
          row.push(borderColor)
          continue
        }
        // 1 像素 = 1 豆，直接采样
        const sx = x - borderThickness
        const sy = y - borderThickness
        const idx = (sy * tmp2.width + sx) * 4
        const alpha = imgData.data[idx + 3]
        row.push(alpha > 127 ? textColor : bgColor)
      }
      grid.push(row)
    }

    const legend = new Map()
    let total = 0
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const id = grid[y][x]
        const color = colorMap.get(id)
        const entry = legend.get(id) || { ...(color || { id, hex: '#cccccc', name: id }), count: 0 }
        entry.count += 1
        legend.set(id, entry)
        total += 1
      }
    }

    return { grid, legend: Array.from(legend.values()), width, height, total, brandId: brand.id }
  }

  useEffect(() => {
    if (!open) return
    const pattern = generatePattern()
    setPreviewPattern(pattern)
    drawPattern(previewCanvasRef.current, pattern, brand)
  }, [open, text, charHeight, textColorId, borderColorId, bgColorId, borderThickness, brand, fontReady])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex w-full max-w-2xl flex-col gap-4 rounded-3xl bg-white p-5 shadow-card max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-cocoa">文字拼豆</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-cocoa-light transition hover:bg-cream-100 hover:text-cocoa"
          >
            ✕
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-cocoa-light">文字内容</label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={20}
                className="form-input"
                placeholder="输入文字"
              />
            </div>

            <OptionGroup
              label={`字高（豆）${fontReady ? '' : ' · 像素字体加载中…'}`}
              value={charHeight}
              onChange={setCharHeight}
              options={[8, 10, 12, 16, 24].map((h) => ({ value: h, label: `${h}` }))}
            />

            <OptionGroup
              label="边框厚度"
              value={borderThickness}
              onChange={setBorderThickness}
              options={[0, 1, 2, 3].map((t) => ({ value: t, label: t === 0 ? '无边框' : `${t} 格` }))}
            />
          </div>

          <div className="space-y-3">
            <SwatchPicker label="文字颜色" value={textColorId} onChange={setTextColorId} colors={brand.colors} />
            <SwatchPicker label="边框颜色" value={borderColorId} onChange={setBorderColorId} colors={borderColors} maxHeight="max-h-20" />
            <SwatchPicker label="背景颜色" value={bgColorId} onChange={setBgColorId} colors={bgColors} maxHeight="max-h-20" />
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-cocoa-light">预览</p>
          <canvas
            ref={previewCanvasRef}
            className="w-full rounded-2xl border border-lavender/30 bg-cream-100"
            style={{ imageRendering: 'pixelated' }}
          />
          <p className="mt-1 text-xs text-cocoa-light">
            {previewPattern?.width || 0}×{previewPattern?.height || 0} · {previewPattern?.legend.length || 0} 种颜色
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-full border border-lavender/60 px-5 py-2 text-sm font-medium text-cocoa transition hover:bg-lavender/30"
          >
            取消
          </button>
          <button
            onClick={() => {
              const pattern = generatePattern()
              if (pattern) {
                onConfirm(pattern)
                onClose()
              }
            }}
            className="rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark hover:shadow-md"
          >
            确认生成
          </button>
        </div>
      </div>
    </div>
  )
}
